import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import styles from './SchoolReadiness.module.css'

const STORAGE_KEY = 'mellomap-school-readiness'
const SCHOOL_TYPES = [
  { key: 'nursery', label: 'Nursery', age: '2.5-3yr', emoji: '' },
  { key: 'lkg', label: 'LKG', age: '3.5-4yr', emoji: '' },
  { key: 'ukg', label: 'UKG', age: '4.5-5yr', emoji: '' },
]

const SKILLS: Record<string, { category: string; emoji: string; items: { skill: string; tip: string }[] }[]> = {
  nursery: [
    { category: 'Communication', emoji: '', items: [
      { skill: 'Responds to own name', tip: 'Play name games — call their name and clap when they look!' },
      { skill: 'Uses 2-3 word phrases', tip: 'Narrate daily activities: "We are eating. Dal is yummy."' },
      { skill: 'Follows simple instructions', tip: 'Start with one-step: "Give me the ball." Then build up.' },
      { skill: 'Expresses needs verbally', tip: 'When they point, model the words: "You want water? Say water!"' },
    ]},
    { category: 'Social', emoji: '', items: [
      { skill: 'Separates from parent briefly', tip: 'Practice short separations at home with a trusted family member.' },
      { skill: 'Plays near other children', tip: 'Arrange park visits — parallel play is the first step!' },
      { skill: 'Shares with prompting', tip: 'Take turns with toys: "My turn, your turn" with a timer.' },
      { skill: 'Takes turns with help', tip: 'Board games and cooking together build turn-taking naturally.' },
    ]},
    { category: 'Self-Help', emoji: '', items: [
      { skill: 'Drinks from cup', tip: 'Start with a small steel cup (lota) — easier to grip!' },
      { skill: 'Eats with spoon', tip: 'Let them practice with dal rice — messy but effective!' },
      { skill: 'Indicates toilet needs', tip: 'Watch for cues and narrate: "Do you need to go potty?"' },
      { skill: 'Attempts to wash hands', tip: 'Step stool at sink + fun soap = independent hand washing.' },
    ]},
    { category: 'Fine Motor', emoji: '', items: [
      { skill: 'Holds crayon', tip: 'Thick crayons are easier — let them scribble freely!' },
      { skill: 'Stacks 4+ blocks', tip: 'Start with 2 blocks, celebrate each new level!' },
      { skill: 'Turns pages', tip: 'Board books at bedtime — they love doing it themselves.' },
      { skill: 'Tears paper', tip: 'Newspaper tearing is great fine motor play!' },
    ]},
  ],
  lkg: [
    { category: 'Communication', emoji: '', items: [
      { skill: 'Speaks in sentences', tip: 'Ask open questions: "What did you do today?" not yes/no.' },
      { skill: 'Tells about experiences', tip: 'At dinner, each family member shares their day.' },
      { skill: 'Follows 2-step instructions', tip: '"Pick up the toy AND put it in the box." Build gradually.' },
      { skill: 'Knows full name', tip: 'Make it a song! Set their full name to a tune they like.' },
    ]},
    { category: 'Social', emoji: '', items: [
      { skill: 'Plays cooperatively', tip: 'Group games like building blocks together build teamwork.' },
      { skill: 'Waits for turn', tip: 'Use a visual timer — waiting is easier when they can see it.' },
      { skill: 'Follows classroom rules', tip: 'Practice at home: "In our house, we sit to eat."' },
      { skill: 'Expresses emotions with words', tip: 'Name emotions throughout the day: "You look happy/sad/angry."' },
    ]},
    { category: 'Academic Readiness', emoji: '', items: [
      { skill: 'Recognizes own name', tip: 'Label their belongings — they\'ll spot their name everywhere!' },
      { skill: 'Counts to 10', tip: 'Count stairs, biscuits, fingers — make numbers part of life.' },
      { skill: 'Knows 4+ colors', tip: 'Color sorting games with dal, buttons, or toys.' },
      { skill: 'Holds pencil correctly', tip: 'Picking up small beads builds the same muscles!' },
      { skill: 'Identifies basic shapes', tip: 'Shape hunt around the house — roti is circle, door is rectangle!' },
    ]},
    { category: 'Self-Help', emoji: '', items: [
      { skill: 'Uses toilet independently', tip: 'A sticker chart for dry days works wonders!' },
      { skill: 'Puts on shoes', tip: 'Velcro shoes first — buckles and laces come later.' },
      { skill: 'Opens lunch box', tip: 'Practice at home before the first day of school!' },
      { skill: 'Carries own bag', tip: 'Start with a light bag — just water bottle and tiffin.' },
    ]},
  ],
  ukg: [
    { category: 'Communication', emoji: '', items: [
      { skill: 'Retells stories', tip: 'After reading, ask "What happened first? Then what?"' },
      { skill: 'Asks questions', tip: 'Encourage curiosity — every "why?" deserves an answer.' },
      { skill: 'Follows multi-step instructions', tip: '3-step treasure hunts build this skill playfully!' },
      { skill: 'Expresses opinions', tip: '"Which dress do you want?" Give real choices daily.' },
    ]},
    { category: 'Academic', emoji: '', items: [
      { skill: 'Writes own name', tip: 'Start with finger writing in sand/salt, then paper.' },
      { skill: 'Counts to 20', tip: 'Count everything — steps, rotis, cars on the road.' },
      { skill: 'Recognizes alphabet', tip: 'Letter of the day — find it on cereal boxes, signs!' },
      { skill: 'Copies patterns', tip: 'Rangoli patterns are perfect for this skill!' },
      { skill: 'Understands big/small/more/less', tip: 'Sorting games and cooking measurements build this.' },
    ]},
    { category: 'Social', emoji: '', items: [
      { skill: 'Resolves conflicts with words', tip: '"Use your words" + model: "I don\'t like that, please stop."' },
      { skill: 'Works in small groups', tip: 'Group art projects at home build collaboration.' },
      { skill: 'Shows empathy', tip: 'Point out feelings in others: "She looks sad. What could we do?"' },
      { skill: 'Manages emotions better', tip: 'A calm corner at home teaches self-regulation.' },
    ]},
    { category: 'Physical', emoji: '', items: [
      { skill: 'Skips', tip: 'Skip together — it takes practice and that\'s okay!' },
      { skill: 'Cuts with scissors', tip: 'Start with play-doh, then thick paper, then thin paper.' },
      { skill: 'Draws recognizable pictures', tip: 'Draw together — narrate what you draw, they\'ll copy.' },
      { skill: 'Writes on lines', tip: 'Wide-ruled notebooks + fat pencils = success!' },
    ]},
  ],
}

function loadChecked(): Record<string, string[]> { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : {} }
function saveChecked(data: Record<string, string[]>) { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)) }

export default function SchoolReadiness() {
  const navigate = useNavigate()
  const childName = useAuthStore(s => s.user?.child_profile?.name) || 'your child'
  const ageMonths = useAuthStore(s => s.user?.child_profile?.age_months) || 36
  const defaultType = ageMonths < 36 ? 'nursery' : ageMonths < 48 ? 'lkg' : 'ukg'
  const [schoolType, setSchoolType] = useState(defaultType)
  const [checked, setChecked] = useState<Record<string, string[]>>(loadChecked)
  const [expanded, setExpanded] = useState<string | null>(null)

  const skills = SKILLS[schoolType]
  const checkedForType = checked[schoolType] || []
  const totalSkills = skills.reduce((sum, cat) => sum + cat.items.length, 0)
  const checkedCount = checkedForType.length
  const progress = totalSkills > 0 ? Math.round((checkedCount / totalSkills) * 100) : 0

  const toggle = (id: string) => {
    const current = checked[schoolType] || []
    const updated = current.includes(id) ? current.filter(c => c !== id) : [...current, id]
    const newChecked = { ...checked, [schoolType]: updated }
    setChecked(newChecked); saveChecked(newChecked)
  }

  const statusLabel = progress >= 75 ? 'Ready!' : progress >= 50 ? 'Almost there!' : 'Keep practicing!'
  const statusColor = progress >= 75 ? 'var(--color-sage)' : progress >= 50 ? '#F1C40F' : 'var(--color-coral)'

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div>
          <h1 className={styles.title}>School Readiness</h1>
          <p className={styles.subtitle}>{childName}</p>
        </div>
      </header>

      <div className={styles.schoolTypes}>
        {SCHOOL_TYPES.map(t => (
          <button key={t.key} className={`${styles.schoolCard} ${schoolType === t.key ? styles.schoolCardActive : ''}`} onClick={() => setSchoolType(t.key)}>
            <span className={styles.schoolEmoji}>{t.emoji}</span>
            <span className={styles.schoolLabel}>{t.label}</span>
            <span className={styles.schoolAge}>{t.age}</span>
          </button>
        ))}
      </div>

      <div className={styles.progressCard}>
        <div className={styles.circleWrap}>
          <svg viewBox="0 0 100 100" className={styles.circleSvg}>
            <circle cx="50" cy="50" r="42" fill="none" stroke="var(--color-cream-dark)" strokeWidth="8" />
            <circle cx="50" cy="50" r="42" fill="none" stroke={statusColor} strokeWidth="8" strokeDasharray={`${progress * 2.64} 264`} strokeLinecap="round" transform="rotate(-90 50 50)" style={{ transition: 'stroke-dasharray 0.5s ease' }} />
          </svg>
          <div className={styles.circleLabel}>
            <span className={styles.circlePercent}>{progress}%</span>
            <span className={styles.circleStatus} style={{ color: statusColor }}>{statusLabel}</span>
          </div>
        </div>
      </div>

      <div className={styles.encouragement}>Your child doesn't need to check every box. Schools are designed to teach these skills too!</div>

      {skills.map(cat => {
        const isOpen = expanded === cat.category
        return (
          <div key={cat.category} className={styles.category}>
            <button className={styles.categoryHeader} onClick={() => setExpanded(isOpen ? null : cat.category)}>
              <span>{cat.emoji} {cat.category}</span>
              <span>{isOpen ? '▾' : '▸'}</span>
            </button>
            {isOpen && (
              <div className={styles.skillList}>
                {cat.items.map((item, i) => {
                  const id = `${schoolType}-${cat.category}-${i}`
                  const isChecked = checkedForType.includes(id)
                  return (
                    <div key={i} className={styles.skillItem}>
                      <label className={`${styles.skillLabel} ${isChecked ? styles.skillChecked : ''}`}>
                        <input type="checkbox" checked={isChecked} onChange={() => toggle(id)} className={styles.checkbox} />
                        <span>{item.skill}</span>
                      </label>
                      {!isChecked && <div className={styles.tipCard}> {item.tip}</div>}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
